/**
 * Institutional Drive containers (ADR 0025 / issue #616).
 *
 * The backend answers 404 for a tenant the capability is not enabled for, so a
 * caller treats "not found" as "no container surface here" rather than an
 * error. That is deliberate: a disabled tenant should not learn the capability
 * exists.
 */
import { api } from "./client";
import { commitOptions } from "./commitSeams";

export type DriveContainerState = "pending" | "ready" | "failed" | "unknown";

export type DriveContainer = {
  id: string;
  session_id: string | null;
  state: DriveContainerState;
  drive_folder_id: string | null;
  folder_name_at_creation: string;
  domain_grant_state: string;
  intent_id: string;
  created_at: string;
  completed_at: string | null;
  editing_setup?: { ready: boolean; checked_at: string; team_pending: number } | null;
};

export type DriveContainerCreated = {
  state: string;
  folder_id: string | null;
  folder_name: string | null;
};

export type CourseDriveSetup = {
  state: "confirmed" | "partial";
  container: DriveContainerCreated;
  editing: "ready" | "blocked" | "unknown" | "pending";
  actor_editing: "queued" | "blocked" | "unknown" | "ready" | "retryable";
  teammates_pending: number;
  access_check_required: boolean;
  recovery?: "institution" | "check" | null;
  targets: { state: "queued" | "blocked" | "unknown" | "ready" | "retryable" }[];
};

/**
 * One query key for every reader of a course's containers, so the affordance
 * that creates one and the material chooser that opens into it share a cache
 * entry rather than issuing two requests that can disagree.
 */
export function driveContainersQueryKey(courseId: string): readonly unknown[] {
  return ["drive-containers", courseId];
}

/**
 * The course container a chooser may open into, or null.
 *
 * Only `ready` qualifies. A `pending` container has no folder yet, and a
 * `failed` or `unknown` one has no folder anyone should be sent to, so each
 * degrades to the unparented chooser rather than to an error.
 */
export function readyCourseFolderId(containers: DriveContainer[]): string | null {
  const course = containers.find(
    (item) => item.session_id === null && item.state === "ready",
  );
  return course?.drive_folder_id ?? null;
}

/** Drive's own folder URL. IMAM stores the ID; the link is derived, never stored. */
export function driveFolderUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${encodeURIComponent(folderId)}`;
}

export const driveContainersApi = {
  setup: (courseId: string, key: string) =>
    api.post<CourseDriveSetup>(`/courses/${courseId}/drive-setup`, {}, commitOptions(key)),
  checkSetup: (courseId: string, key: string) =>
    api.post<CourseDriveSetup>(`/courses/${courseId}/drive-setup/check`, {}, commitOptions(key)),
  retrySetup: (courseId: string, key: string) =>
    api.post<CourseDriveSetup>(`/courses/${courseId}/drive-setup/retry`, {}, commitOptions(key)),
  list: (courseId: string) =>
    api.get<{ containers: DriveContainer[] }>(
      `/courses/${courseId}/drive-containers`,
    ),
  /** The course folder's create carries its command key; Check reads the receipt for it (W1). */
  createForCourse: (courseId: string, key: string) =>
    api.post<DriveContainerCreated>(`/courses/${courseId}/drive-container`, {}, commitOptions(key)),
  createForSession: (sessionId: string) =>
    api.post<DriveContainerCreated>(`/sessions/${sessionId}/drive-container`, {}),
  reconcile: (containerId: string) =>
    api.post<{ state: string; resolution: string; folder_id: string | null }>(
      `/drive-containers/${containerId}/reconcile`,
      {},
    ),
};
